/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The trust strip above the comparison list (docs/plan-vergelijker-resultaten-
 * 2026-09-30.md §12.4 B1): four facts a visitor weighs a comparison site by,
 * each one line with its proof one tap away — one row from `lg`, two by two
 * from `sm`, one column below it. In a 2×2 at 375 px the first item ran to five
 * lines in its half-width cell; one column gives every fact the full width (two
 * lines at most) with a tighter row gap, and the block above the list came out
 * 3 px shorter than the 2×2 (NL, measured 2026-10-01).
 *
 *   1. What orders the list, and that payment never does. This item IS the
 *      ranking disclosure (BW 6:193e lid 2, Art. 7(4a) UCPD): the main
 *      parameter, stated where the results are, the full method one link away
 *      under that page's own name (`nav.how_we_compare`). It must stay in the
 *      baked HTML for every reader — never behind a toggle, never client-only.
 *   2. How the site earns, linked to the method page's revenue section.
 *   3. Coverage: the offers and the providers in the comparison, linked to the
 *      explanation of why the NEa register holds more parties than this list.
 *   4. When the offers were last written (the providers file's write time,
 *      `GET /api/providers/meta` / the bake's `providersMeta`) — not a per-row
 *      date and not the dossier's "gecontroleerd op", which are other claims.
 *      For a visitor back on another day to a comparison in which nothing
 *      changed, the same item adds "· ongewijzigd sinds je bezoek van {visit}"
 *      (`lastVisitAt`, plan §12.4 B3): the quiet clause in place of a changes
 *      strip with nothing to say. The engine passes it from its changes state,
 *      which only an effect fills — never in the bake or the first client
 *      render, so the baked item and the hydrating one are the same text.
 *
 * Below `sm` the strip folds to item 1 (2026-10-14, Jasper: "ensure that the
 * Stap 2 header is compressed, it takes up a lot of scroll on mobile"). Item 1
 * ends in a "Meer info" button there, and items 2–4 show when it is pressed:
 * the four items stood 148 px tall in their one column, between a "Vergelijken"
 * tap and the first result. Folded is `hidden sm:flex` on the three, never a
 * condition on their markup, so every item stays in the baked HTML and every
 * reader without CSS still gets all four; item 1 itself never folds. Whether
 * the three show is the engine's state (`phoneMoreOpen`), kept per history
 * entry with the open rows (src/lib/viewState.ts): the fold moves every row
 * under it, and a Back must find the list where the scroll position left it.
 *
 * Static markup: no motion, no effect, no window, no Date.now(). The date is
 * formatted in the site's own timezone (formatDateShort), so the bake and the
 * hydrating browser print the same day. Item 4 renders only with a date: a
 * shell-served (filtered) URL has no seed and shows it once the engine's fetch
 * has answered; the grid keeps its cell, so nothing below moves.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, CalendarCheck, ChevronDown, HandCoins, Scale } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { cn } from '../../lib/cn';
import { formatDateShort, formatInteger } from '../../lib/formatters';
import { COVERAGE_ANCHOR, REVENUE_MODEL_ANCHOR } from '../../lib/orgIdentity';
import { fillTemplate } from '../../lib/templateVars.js';
import { providerHubPath } from '../../lib/providerRoutes.js';

export interface TrustStripProps {
  /** Offers (rows) in the comparison — every audience, not the filtered view. */
  offerCount: number;
  /** Distinct providers behind those offers. */
  providerCount: number;
  /** When the providers file was last written (ISO 8601); null = not known (yet). */
  updatedAt: string | null;
  /**
   * The visitor's previous visit (epoch ms), passed only when it was on an
   * earlier day and nothing in the comparison changed since: item 4 then says
   * so. Absent or null at the bake and on the first client render.
   */
  lastVisitAt?: number | null;
  /**
   * Below `sm`: whether items 2–4 show (the "Meer info" button's state, which
   * the engine keeps per history entry). Ignored from `sm`, where all four
   * always show.
   */
  phoneMoreOpen?: boolean;
  /** Toggles `phoneMoreOpen`. Without it the strip does not fold on a phone either. */
  onPhoneMoreToggle?: () => void;
}

const ICON_CLASS = 'w-3.5 h-3.5 mt-px shrink-0 text-emerald-500';

/** Items 2–4, the ones the phone fold hides — named by the button that shows them. */
const FOLDED_ITEM_IDS = {
  fee: 'trust-strip-fee',
  coverage: 'trust-strip-coverage',
  updated: 'trust-strip-updated',
} as const;

/**
 * The phone's "Meer info" button at the end of item 1: the line's own colour
 * and weight up one step, so it reads as part of the strip and not as a third
 * link; its chevron says it unfolds. 24 px of hit height inside a 16 px line
 * (the negative margin keeps the line where it was).
 */
const MORE_BUTTON_CLASS =
  'inline-flex items-center gap-0.5 whitespace-nowrap -my-1 py-1 font-bold hover:text-emerald-400 [html.light_&]:hover:text-emerald-700 focus-ring rounded-sm transition-colors';

/** A no-break space: the date ("1 okt 2026") never breaks across two lines. */
const NBSP = String.fromCharCode(0xa0);

/**
 * The method link of item 1 — the disclosure's own link, styled as it was under
 * the paragraph it replaces. Light: emerald-700 (5.4:1 on the page, measured
 * 2026-10-01); the emerald-400 token is remapped to #059669 there, 3.8:1, too
 * pale for 12 px.
 */
const METHOD_LINK_CLASS =
  'whitespace-nowrap font-bold text-emerald-400 [html.light_&]:text-emerald-700 underline underline-offset-2 hover:text-emerald-300 focus-ring rounded-sm';

/**
 * A whole item as its link (2 and 3): the line's own colour, so the strip
 * reads as four facts, and an underline — never colour alone — that says it
 * leads somewhere.
 */
const ITEM_LINK_CLASS =
  'underline decoration-slate-500/70 underline-offset-[3px] hover:text-emerald-400 [html.light_&]:hover:text-emerald-700 hover:decoration-current focus-ring rounded-sm transition-colors';

export const TrustStrip: React.FC<TrustStripProps> = ({
  offerCount,
  providerCount,
  updatedAt,
  lastVisitAt = null,
  phoneMoreOpen = false,
  onPhoneMoreToggle,
}) => {
  const { t, language } = useLanguage();
  const date = updatedAt ? formatDateShort(updatedAt, language).replace(/ /g, NBSP) : '';
  const visit =
    lastVisitAt !== null
      ? formatDateShort(new Date(lastVisitAt), language).replace(/ /g, NBSP)
      : '';
  // The " · " stays with the date before it, so a wrapped line never starts with it.
  const updatedText = visit
    ? fillTemplate(t('comparison.trust_updated_unchanged'), { date, visit }).replace(
        / ·/g,
        `${NBSP}·`
      )
    : fillTemplate(t('comparison.trust_updated'), { date });

  const folds = onPhoneMoreToggle !== undefined;
  // Items 2–4: shown from sm; below it only once "Meer info" is pressed.
  const foldedItemClass = cn(
    'items-start gap-1.5 min-w-0',
    folds && !phoneMoreOpen ? 'hidden sm:flex' : 'flex'
  );
  const foldedIds = [
    FOLDED_ITEM_IDS.fee,
    FOLDED_ITEM_IDS.coverage,
    ...(date ? [FOLDED_ITEM_IDS.updated] : []),
  ].join(' ');

  return (
    <ul
      data-trust-strip
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-2 sm:gap-y-2.5 lg:gap-x-6 border-y border-slate-700/40 py-3 text-xs leading-snug font-medium text-slate-300 text-pretty"
    >
      <li className="flex items-start gap-1.5 min-w-0">
        <Scale className={ICON_CLASS} aria-hidden="true" />
        <span className="min-w-0">
          {t('comparison.trust_order')}{' '}
          <Link to={`/${language}/hoe-wij-vergelijken`} className={METHOD_LINK_CLASS}>
            {t('nav.how_we_compare')}
          </Link>
          {/* The phone fold's button. The no-break space keeps the dot with
            the link, so a wrapped line never starts with it. */}
          {folds && (
            <span data-trust-more className="sm:hidden">
              <span aria-hidden="true">{`${NBSP}· `}</span>
              {/* Named in full by one key: "Meer info" alone says nothing out
                of context, and a sr-only tail lost its leading space in
                engines that blockify it. The label starts with the words in
                sight (label in name). */}
              <button
                type="button"
                aria-label={t('comparison.trust_more_aria')}
                aria-expanded={phoneMoreOpen}
                aria-controls={foldedIds}
                onClick={onPhoneMoreToggle}
                className={MORE_BUTTON_CLASS}
              >
                {t('comparison.trust_more')}
                <ChevronDown
                  className={cn(
                    'w-3 h-3 shrink-0 transition-transform motion-reduce:transition-none',
                    phoneMoreOpen && 'rotate-180'
                  )}
                  aria-hidden="true"
                />
              </button>
            </span>
          )}
        </span>
      </li>
      <li id={FOLDED_ITEM_IDS.fee} className={foldedItemClass}>
        <HandCoins className={ICON_CLASS} aria-hidden="true" />
        <Link
          to={`/${language}/hoe-wij-vergelijken#${REVENUE_MODEL_ANCHOR}`}
          className={ITEM_LINK_CLASS}
        >
          {t('comparison.trust_fee')}
        </Link>
      </li>
      <li id={FOLDED_ITEM_IDS.coverage} className={foldedItemClass}>
        <Building2 className={ICON_CLASS} aria-hidden="true" />
        <Link to={`${providerHubPath(language)}#${COVERAGE_ANCHOR}`} className={ITEM_LINK_CLASS}>
          {fillTemplate(t('comparison.trust_coverage'), {
            offers: formatInteger(offerCount, language),
            providers: formatInteger(providerCount, language),
          })}
        </Link>
      </li>
      {date && (
        <li id={FOLDED_ITEM_IDS.updated} className={foldedItemClass}>
          <CalendarCheck className={ICON_CLASS} aria-hidden="true" />
          <span className="min-w-0">{updatedText}</span>
        </li>
      )}
    </ul>
  );
};
